import {
  Component,
  HostListener,
  Input,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { PLATFORM_ICONS } from '../../../helpers/constants/platform-icons.constant';
import { CommonModule } from '@angular/common';
import { WorkflowService } from '../../../api/services/workflow.service';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';

import { Router } from '@angular/router';
import { UserPrefsService } from '../../../core/services/user-prefs.service';
import { AuthService } from '../../../api/services/auth.service';
import { NotificationHelperService } from '../../../helpers/notification-helper.service';

@Component({
  selector: 'app-login-cards',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './login-cards.component.html',
  styleUrl: './login-cards.component.css',
})
export class LoginCardsComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  isLoading: boolean = false;
  endpoint = environment.api_endpoint;
  fieldsData: any;
  activeModalContext: any;
  loadingCards: Set<string> = new Set();
  selectedOptions: any[] = [];
  accessToken: any;
  workflowData: any;
  currentRecordId: string | undefined;
  workflowId: any;
  currentNodeId: any;
  currentNode: any;
  recordID: any;
  nextNodes: any;
  targetWorkflowId: any;
  targetValue: any;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;
  showValidationError: boolean = false;
  modalTitle: any;
  formData: any;
  private pollingInterval: any;
  private isVerifying: boolean = false;
  private authWindowInterval: any;
  private authWindow: Window | null = null;

  constructor(
    private formService: FormsServiceService,
    private workflowService: WorkflowService,
    private router: Router,
    private userPrefsService: UserPrefsService,
    private authService: AuthService,
    private notificationHelper: NotificationHelperService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.getData();
    window.addEventListener('message', this.messageListener);
    this.startLocalStoragePolling();
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.messageListener);
    this.stopLocalStoragePolling();
    if (this.authWindowInterval) {
      clearInterval(this.authWindowInterval);
    }
  }

  startLocalStoragePolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    this.pollingInterval = setInterval(() => {
      // 1. Try to read keys from popup localStorage if same-origin
      try {
        if (
          this.authWindow &&
          !this.authWindow.closed &&
          this.authWindow.localStorage
        ) {
          const wAccessToken =
            this.authWindow.localStorage.getItem('accessToken') ||
            this.authWindow.localStorage.getItem('token');
          const wEmail = this.authWindow.localStorage.getItem('email');
          const wRefreshToken =
            this.authWindow.localStorage.getItem('refreshToken');
          const wRole = this.authWindow.localStorage.getItem('role');
          const wPmSetup = this.authWindow.localStorage.getItem('pmSetup');
          if (wAccessToken) {
            localStorage.setItem('accessToken', wAccessToken);
            if (wEmail) localStorage.setItem('email', wEmail);
            if (wRefreshToken)
              localStorage.setItem('refreshToken', wRefreshToken);
            if (wRole) localStorage.setItem('role', wRole);
            if (wPmSetup) localStorage.setItem('pmSetup', wPmSetup);
          }
        }
      } catch (_) {}

      const accessToken =
        localStorage.getItem('accessToken') || localStorage.getItem('token');
      const email = localStorage.getItem('email') || '';
      const refreshToken = localStorage.getItem('refreshToken') || '';
      const role = localStorage.getItem('role') || '';
      const pmSetup = localStorage.getItem('pmSetup') || '';

      if (accessToken) {
        if (this.isVerifying) {
          return;
        }
        this.isVerifying = true;
        this.stopLocalStoragePolling();
        this.loadingCards.clear();
        this.verifyToken({
          email,
          accessToken,
          refreshToken,
          role,
          pmSetup,
        });
      }
    }, 1000);
  }

  stopLocalStoragePolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Polls localStorage every second until the required keys appear (max 120 s).
   * Used after opening the SSO auth URL in a new window.
   * Once all keys are present the current window navigates to targetUrl.
   */
  private pollForTokens(
    requiredKeys: string[],
    targetUrl: string,
    authWindow?: Window | null,
    maxSeconds = 120,
  ): void {
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed++;

      // 1. Try to read keys from the popup's localStorage if they are same-origin
      try {
        if (authWindow && !authWindow.closed && authWindow.localStorage) {
          const popupKeysPresent = requiredKeys.every(
            (k: string) => !!authWindow.localStorage!.getItem(k),
          );
          if (popupKeysPresent) {
            requiredKeys.forEach((k: string) => {
              const val = authWindow.localStorage!.getItem(k);
              if (val) localStorage.setItem(k, val);
            });
            const rt = authWindow.localStorage!.getItem('refreshToken');
            if (rt) localStorage.setItem('refreshToken', rt);
            const role = authWindow.localStorage!.getItem('role');
            if (role) localStorage.setItem('role', role);
          }
        }
      } catch (_) {
        // Cross-origin: ignore security block during redirect phase
      }

      // 2. Check if all required keys are present in parent window's localStorage
      const allPresent = requiredKeys.every(
        (k: string) => !!localStorage.getItem(k),
      );
      if (allPresent) {
        clearInterval(timer);
        // Close the popup if it is still open
        if (authWindow && !authWindow.closed) {
          try {
            authWindow.close();
          } catch (_) {}
        }
        if (typeof window !== 'undefined') {
          try {
            window.focus();
          } catch (_) {}
        }

        const origin = window.location.origin;
        if (targetUrl.startsWith(origin)) {
          const relativePath = targetUrl.substring(origin.length);
          this.router.navigateByUrl(relativePath);
        } else if (targetUrl.startsWith('http')) {
          window.location.href = targetUrl;
        } else {
          this.router.navigateByUrl(targetUrl);
        }
      } else if (elapsed >= maxSeconds) {
        clearInterval(timer);
        console.warn('[Auth] Timed out waiting for SSO tokens.');
      }
    }, 1000);
  }

  /** Function to get the data by api or static */
  getData() {
    this.fieldsData = [
      {
        _comboRank: 3,
        _id: '6918548d2455fe41fd1af33c',
        category: 'Identity Management',
        categoryCode: 'IAM-GWS',
        description:
          'Cloud-based productivity and identity management suite by Google.',
        fieldDependencies: [
          {
            condition: [
              {
                action: 'connect',
                conditions: [
                  {
                    targetType: 'workflow',
                    targetValue: '6a0c028af23eb2ec6a7f8b83',
                    type: 'modal',
                  },
                ],
              },
            ],
            trigger: 'onClick',
          },
        ],
        iconUrl:
          'https://developmentcontent.blob.core.windows.net/resources/infrastructure/GWS.png',
        location: 'Cloud',
        location_id: '',
        platformName: 'Google Workspace',
        selected: true,
        status: 'Disconnected',
        warning: false,
        workflowId: '',
      },
      {
        _comboRank: 3,
        _id: '6918548d2455fe41fd1af32b',
        category: 'Identity Management',
        categoryCode: 'IAM-ENTRA',
        description: 'Identity and access management solution by Microsoft.',
        fieldDependencies: [
          {
            condition: [
              {
                action: 'connect',
                conditions: [
                  {
                    targetType: 'workflow',
                    targetValue: '6a0c052af23eb2ec6a7f8b85',
                    type: 'modal',
                  },
                ],
              },
            ],
            trigger: 'onClick',
          },
        ],
        iconUrl: '/assets/media/integration/microsoft.png',
        location: 'Cloud',
        location_id: '',
        platformName: 'Microsoft Entra',
        selected: true,
        status: 'Connected',
        updatedAt: '2026-05-14T12:34:46.052Z',
        warning: false,
        workflowId: '',
      },
      // {
      //   _comboRank: 3,
      //   _id: '6918548d2455fe41fd1af32b',
      //   category: 'Identity Management',
      //   categoryCode: 'IAM-ENTRA',
      //   description: 'Identity and access management solution by Microsoft.',
      //   fieldDependencies: [
      //     {
      //       condition: [
      //         {
      //           action: 'connect',
      //           conditions: [
      //             {
      //               targetType: 'workflow',
      //               targetValue: '6a0c052af23eb2ec6a7f8b85',
      //               type: 'modal',
      //             },
      //           ],
      //         },
      //       ],
      //       trigger: 'onClick',
      //     },
      //   ],
      //   iconUrl: '/assets/media/integration/microsoft.png',
      //   location: 'Cloud',
      //   location_id: '',
      //   platformName: 'CISCO Duo',
      //   selected: true,
      //   status: 'Connected',
      //   updatedAt: '2026-05-14T12:34:46.052Z',
      //   warning: false,
      //   workflowId: '',
      // },
    ];
  }

  getPlatformIcon(option: any): string {
    return (
      option?.iconUrl ||
      PLATFORM_ICONS[option?.platformName] ||
      PLATFORM_ICONS['default']
    );
  }

  onImageError(event: Event, option: any) {
    const element = event.target as HTMLImageElement;

    element.src =
      PLATFORM_ICONS[option?.platformName] || PLATFORM_ICONS['default'];
  }

  get control() {
    return this.formGroup.get(this.fields.fieldName);
  }

  /** Function to toggle the card and push the selection into array */
  toggleSelection(option: any): void {
    // if (this.fields?.fieldType !== 'singleSelect') return;;
    if (this.authWindow && !this.authWindow.closed) {
      try {
        this.authWindow.close();
      } catch (_) {}
      this.authWindow = null;
    }
    if (this.authWindowInterval) {
      clearInterval(this.authWindowInterval);
      this.authWindowInterval = null;
    }

    const optionId = option?._id?.toString();
    if (!optionId) return;
    this.loadingCards.clear();
    this.loadingCards.add(optionId);
    this.openConnect(option, optionId);
  }

  findSseConfig(obj: any): any {
    if (!obj || typeof obj !== 'object') return null;
    if (obj.sseConfig) return obj.sseConfig;

    // search inside arrays (like fieldDependencies)
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const found = this.findSseConfig(item);
        if (found) return found;
      }
      return null;
    }

    // search inside object keys
    for (const key of Object.keys(obj)) {
      if (typeof obj[key] === 'object') {
        const found = this.findSseConfig(obj[key]);
        if (found) return found;
      }
    }
    return null;
  }

  handleSseConfigAuth(sseConfig: any, option: any, _unused?: Window | null) {
    if (!sseConfig) {
      this.loadingCards.delete(option._id);
      return;
    }

    let authUrl = sseConfig.integration?.authUrl;
    if (!authUrl) {
      this.loadingCards.delete(option._id);
      return;
    }

    authUrl = this.formService.replaceTemplateTokens(authUrl, {
      base: this.endpoint,
      api_endpoint: this.endpoint,
    });

    this.authWindow = window.open(
      authUrl,
      'oauthWindow',
      'width=700,height=800,left=200,top=100',
    );

    if (!this.authWindow) {
      this.loadingCards.delete(option._id);
      return;
    }

    if (this.authWindowInterval) {
      clearInterval(this.authWindowInterval);
    }

    this.authWindowInterval = setInterval(() => {
      if (this.authWindow && this.authWindow.closed) {
        clearInterval(this.authWindowInterval);
        this.authWindowInterval = null;
        this.loadingCards.delete(option._id);
        this.cdr.detectChanges();

        if (typeof window !== 'undefined') {
          try {
            window.focus();
          } catch (_) {}
        }

        // When popup closes, check if tokens arrived in localStorage
        const accessToken =
          localStorage.getItem('accessToken') || localStorage.getItem('token');
        if (accessToken && !this.isVerifying) {
          this.isVerifying = true;
          this.stopLocalStoragePolling();
          const email = localStorage.getItem('email') || '';
          const refreshToken = localStorage.getItem('refreshToken') || '';
          const role = localStorage.getItem('role') || '';
          const pmSetup = localStorage.getItem('pmSetup') || '';
          this.verifyToken({
            email,
            accessToken,
            refreshToken,
            role,
            pmSetup,
          });
        }
      }
    }, 1000);
  }

  private messageListener = (event: MessageEvent) => {
    let isAllowedOrigin = false;
    try {
      const expectedOrigin = new URL(this.endpoint).origin;
      const currentOrigin = window.location.origin;
      isAllowedOrigin =
        event.origin === expectedOrigin ||
        event.origin === currentOrigin ||
        event.origin.includes('eciso.tech');
    } catch (_) {
      isAllowedOrigin = true;
    }

    if (!isAllowedOrigin) {
      return;
    }

    const { email, accessToken, token, refreshToken, role, pmSetup } =
      event.data || {};

    const validToken = accessToken || token;
    if (!validToken) {
      return;
    }

    if (this.isVerifying) {
      return;
    }
    this.isVerifying = true;
    this.stopLocalStoragePolling();
    if (this.authWindowInterval) {
      clearInterval(this.authWindowInterval);
      this.authWindowInterval = null;
    }
    this.loadingCards.clear();

    if (email) localStorage.setItem('email', email);
    localStorage.setItem('accessToken', validToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (role) localStorage.setItem('role', role);
    if (pmSetup) localStorage.setItem('pmSetup', pmSetup);

    this.verifyToken({
      email: email || '',
      accessToken: validToken,
      refreshToken: refreshToken || '',
      role: role || '',
      pmSetup: pmSetup || '',
    });
  };

  verifyToken(payload: any) {
    this.authService.verifyToken(payload.accessToken).subscribe({
      next: async (res: any) => {
        localStorage.setItem('user', JSON.stringify(res.data));
        localStorage.setItem('token', res.token);
        if (payload.accessToken) {
          localStorage.setItem('accessToken', payload.accessToken);
        }

        const userId =
          res?.data?._id ||
          res?.data?.id ||
          res?.data?.userId ||
          payload?.userId ||
          '';
        if (userId) {
          localStorage.setItem('userId', userId);
        }

        const subId = res?.data?.subscriptionId;
        if (subId) {
          localStorage.setItem('subscriptionId', String(subId));
        }

        const ssoToken =
          payload?.accessToken ||
          res?.token ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('token') ||
          '';

        // Close the opened popup window on success FIRST
        if (this.authWindow && !this.authWindow.closed) {
          try {
            this.authWindow.close();
          } catch (_) {}
          this.authWindow = null;
        }

        // Return focus to the main window
        if (typeof window !== 'undefined') {
          try {
            window.focus();
          } catch (_) {}
        }

        // Set login flag so push notification prompt can be triggered on landing
        sessionStorage.setItem('just_logged_in', 'true');
        localStorage.setItem('just_logged_in', 'true');

        this.router
          .navigate(['/eciso/executive-dashboard'])
          .then((navigated) => {
            if (!navigated) {
              window.location.href = '/eciso/executive-dashboard';
            }
          });
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isVerifying = false;
        this.loadingCards.clear();
        // Clean stale/failed keys to avoid infinite loop
        localStorage.removeItem('accessToken');
        localStorage.removeItem('pmSetup');
        localStorage.removeItem('email');
        localStorage.removeItem('refreshToken');
        this.startLocalStoragePolling();
        this.cdr.detectChanges();
      },
    });
  }

  /** @deprecated Use handleSseConfigAuth which opens a new SSO window instead. */
  pollLocalStorage(onSuccess: any, optionId: string) {
    const conditionGroup = onSuccess.condition?.[0];
    const conditions = conditionGroup?.conditions || [];
    let requiredKeys: string[] = conditions
      .filter((c: any) => c.type === 'localStorage')
      .map((c: any) => c.value as string)
      .filter(Boolean);

    if (requiredKeys.length === 0) {
      requiredKeys = ['accessToken', 'pmSetup', 'email'];
    }

    let targetUrl: string =
      onSuccess.targetValue || '/eciso/executive-dashboard';
    if (targetUrl === '/admin/dashboard' || targetUrl === 'scc') {
      targetUrl = '/eciso/executive-dashboard';
    }
    if (targetUrl && !targetUrl.startsWith('http')) {
      const isPublicRoute =
        targetUrl.startsWith('/login') ||
        targetUrl.startsWith('/error') ||
        targetUrl.startsWith('/form') ||
        targetUrl.startsWith('/workflow');

      if (!isPublicRoute && !targetUrl.startsWith('/eciso')) {
        targetUrl = targetUrl.startsWith('/')
          ? `/eciso${targetUrl}`
          : `/eciso/${targetUrl}`;
      }

      const baseUrl = window.location.origin;
      targetUrl = `${baseUrl}${targetUrl.startsWith('/') ? '' : '/'}${targetUrl}`;
    }

    this.pollForTokens(requiredKeys, targetUrl);
    this.loadingCards.delete(optionId);
  }

  resetSelection(optionId: string, tagsControl: any): void {
    this.control?.setValue([]);
    tagsControl?.setValue([]);
    this.selectedOptions = [];
    this.loadingCards.delete(optionId);
  }

  handleWorkflowModal(option: any): boolean {
    const fieldDeps = option?.fieldDependencies || [];
    const onClickDep = fieldDeps.find((d: any) => d.trigger === 'onClick');
    if (!onClickDep?.condition) return false;

    for (const block of onClickDep.condition) {
      const modalCond = block.conditions?.find(
        (c: any) =>
          c.type === 'modal' && c.targetType === 'workflow' && c.targetValue,
      );

      if (!modalCond) continue;

      this.activeModalContext = {
        workflowId: modalCond.targetValue,
        selectedOption: option,
        authUrl: option.integration?.authUrl,
        selectedName: option.name,
        fieldName: this.fields.fieldName,
        currentSelectedName:
          option.name == 'Cloud Computing' ? 'GCP' : option.name,
      };

      this.loadWorkflow(modalCond.targetValue, 0);

      const modal = document.getElementById(
        `connection_modal_${this.fields.fieldName}`,
      );
      modal?.classList.add('show');

      return true;
    }

    return false;
  }

  /** Loads the workflow and optionally a specific node */
  loadWorkflow(wId: string, nodeId?: any, nodeIndex?: any): void {
    this.workflowService.loadWorkflow(wId).subscribe({
      next: ({ workflowData, recordId }) => {
        this.workflowData = workflowData;
        this.currentRecordId = recordId;
        this.workflowId = wId;
        // --- Handle second last node request ---
        if (nodeIndex === 'secondLastNode') {
          const nodes = workflowData?.nodes || [];

          if (nodes.length >= 2) {
            const secondLastNode = nodes[nodes.length - 2]; // ⭐ correct node
            this.currentNodeId = secondLastNode._id;
            this.loadNode(secondLastNode._id, recordId);
            return; // ⭐ STOP HERE, do NOT load startNodeId
          }
        }
        // --- Default start node logic ---
        const startNodeId = nodeId || workflowData?.startNodeId;
        if (startNodeId) {
          this.currentNodeId = startNodeId;
          this.loadNode(startNodeId, recordId);
        } else {
          console.warn(`[Workflow] No startNodeId found for workflow: ${wId}`);
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.cdr.detectChanges();
      },
    });
  }

  /** Loads a node and processes navigation-related metadata */
  loadNode(nodeId: string, recordId?: string): void {
    const node = this.workflowData?.nodes?.find((n: any) => n._id === nodeId);
    if (!node) {
      return;
    }

    this.currentNode = node;
    this.currentNodeId = node._id;
    this.recordID = node.recordId || recordId;
    if (
      this.currentNode?.triggers?.some(
        (trigger: any) => trigger.type === 'api' && trigger.event === 'onLoad',
      )
    ) {
      this.executeNodeTriggers();
    }
    const nextButtonObj =
      node.next?.find((n: any) =>
        ['next_button', 'submit_continue'].includes(n.buttonId),
      ) || node.next?.[0];

    if (!nextButtonObj) return;

    const condition = nextButtonObj.condition;

    if (Array.isArray(condition)) {
      for (const cond of condition) {
        if (cond?.targetType === 'node' && cond?.targetValue) {
          this.nextNodes = cond.targetValue;
          return;
        }
        if (cond?.targetNodeId) {
          this.nextNodes = cond.targetNodeId;
          return;
        }
        if (cond?.targetType === 'url' && cond?.targetValue) {
          this.targetValue = cond.targetValue;
          return;
        }
        if (cond?.targetType === 'workflow' && cond?.targetValue) {
          this.targetWorkflowId = cond.targetValue;
          return;
        }
      }
    } else if (typeof condition === 'object' && condition !== null) {
      if (nextButtonObj.targetType === 'node' && nextButtonObj.targetValue) {
        this.nextNodes = nextButtonObj.targetValue;
      } else if (nextButtonObj.targetNodeId) {
        this.nextNodes = nextButtonObj.targetNodeId;
      } else if (
        nextButtonObj.targetType === 'url' &&
        nextButtonObj.targetValue
      ) {
        this.targetValue = nextButtonObj.targetValue;
      } else if (
        nextButtonObj.targetType === 'workflow' &&
        nextButtonObj.targetValue
      ) {
        this.targetWorkflowId = nextButtonObj.targetValue;
      } else {
        console.warn(`[Workflow] No valid condition in nextButtonObj.`);
      }
    } else {
      console.warn(`[Workflow] Invalid or missing condition in node.`);
    }
  }

  executeNodeTriggers() {
    const node = this.workflowData?.nodes?.find(
      (n: any) => n._id === this.currentNodeId,
    );

    if (!node?.triggers?.length) return;

    const onLoadTrigger = node.triggers.find(
      (trigger: any) => trigger.type === 'api' && trigger.event === 'onLoad',
    );

    if (!onLoadTrigger?.api) return;

    const triggerApi = onLoadTrigger.api;

    // replace template tokens
    const url = this.formService.replaceTemplateTokens(triggerApi.url, {
      api_endpoint: this.endpoint,
      formID: triggerApi.formID,
      recordID: node.recordId || '',
    });

    this.formService
      .submitFormData(
        {
          url,
          method: triggerApi.method || 'GET',
        },
        {},
      )
      .subscribe({
        next: (res) => {
          // success
          this.handleWorkflowTransition(200);
          this.cdr.detectChanges();
        },

        error: (err) => {
          // error transition
          this.handleWorkflowTransition(err.status);
          this.cdr.detectChanges();
        },
      });
  }

  handleWorkflowTransition(status: number) {
    const node = this.workflowData?.nodes?.find(
      (n: any) => n._id === this.currentNodeId,
    );

    if (!node?.next?.length) return;

    const transitions = node.next[0].condition || [];

    for (const condition of transitions) {
      const apiCondition = condition.conditions?.find(
        (c: any) => c.type === 'api' && c.value === 'status',
      );

      if (apiCondition?.equals?.includes(status)) {
        // update active node
        this.currentNodeId = condition.targetValue;

        // persist workflow state
        this.workflowData = {
          ...this.workflowData,
          startNodeId: condition.targetValue,
        };

        // update current node
        this.currentNode = this.workflowData?.nodes?.find(
          (n: any) => n._id === condition.targetValue,
        );

        // save workflow instance
        const workflowId = this.workflowData?._id || this.workflowId;

        if (workflowId) {
          const apiUrl = `/api/v1/workflow/instances/${workflowId}`;

          this.modalRendererRef.updateInstance(
            apiUrl,
            this.workflowData,
            false,
          );
        }

        break;
      }
    }
  }

  /** Function to validate the card is selected or not then show error message */
  validateSelection(): boolean {
    const currentValue = (this.control?.value || []).map((v: any) =>
      v.toString(),
    );
    if (currentValue.length > 0) {
      this.showValidationError = false;
      return true;
    }
    if (this.fields.isRequired) {
      this.showValidationError = true;
      return false;
    }
    this.showValidationError = false;
    return true;
  }

  openConnect(option: any, id: string) {
    const fieldDeps = option?.fieldDependencies || [];
    const onClickDep = fieldDeps.find((dep: any) => dep.trigger === 'onClick');

    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'workflow' && c.targetValue,
        );

        if (modalCond) {
          // No popup needed — OAuth runs in the same window.

          // --- Set modal context for <app-modal-renderer> ---
          this.activeModalContext = {
            workflowId: modalCond.targetValue,
            selectedOption: option,
            selectedName: option.name,
            authUrl: option.integration?.authUrl || null,
            recordID: this.recordID,
            workflowData: this.workflowData,
          };

          // --- Load the workflow from API to check the first node ---
          this.workflowService.loadWorkflow(modalCond.targetValue).subscribe({
            next: ({ workflowData, recordId }) => {
              this.workflowData = workflowData;
              this.currentRecordId = recordId;
              this.workflowId = modalCond.targetValue;

              const startNodeId = workflowData?.startNodeId;

              const processSseConfig = () => {
                const sseConfig =
                  this.formData?.sseConfig ||
                  this.findSseConfig(workflowData) ||
                  this.findSseConfig(option) ||
                  this.findSseConfig(this.fields);

                if (sseConfig) {
                  // null = no popup; handleSseConfigAuth will navigate in-place
                  this.handleSseConfigAuth(sseConfig, option, null);
                } else {
                  // 🧩 No sseConfig — open the inline modal and load first node
                  this.currentNodeId = startNodeId;
                  this.loadNode(startNodeId, recordId);

                  const modalId = `connection_modal_${this.fields.fieldName}`;
                  const modal = document.getElementById(modalId);
                  if (modal) {
                    modal.classList.add('show');
                  }
                  this.loadingCards.delete(id);
                }
              };

              // The startNodeId itself serves as the formId for loading the form JSON
              if (startNodeId) {
                this.loadForm(startNodeId, recordId).subscribe({
                  next: () => {
                    processSseConfig();
                    this.cdr.detectChanges();
                  },
                  error: () => {
                    processSseConfig();
                    this.cdr.detectChanges();
                  }, // fallback if form load fails
                });
              } else {
                processSseConfig();
              }
              this.cdr.detectChanges();
            },
            error: (error: any) => {
              this.loadingCards.delete(id);
              this.cdr.detectChanges();
            },
          });
          return;
        }
      }
    }

    // If no workflow was found to load
    this.loadingCards.delete(id);
  }

  /** Function to set the modal title */
  setModalTitle(title: string) {
    this.modalTitle = title;
  }

  checkPollingServiceForError(value: any) {
    // 🔍 Find the "attempts" condition in the current node’s next array
    const attemptNode = this.currentNode?.next?.find((next: any) =>
      next.condition.some((condGroup: any) =>
        condGroup.conditions.some((cond: any) => cond.type === 'attempts'),
      ),
    );

    if (attemptNode) {
      const targetNodeId = attemptNode?.condition?.[0]?.targetValue;
      const targetType = attemptNode?.condition?.[0]?.targetType;
      if (targetType === 'node' && targetNodeId) {
        this.loadNode(targetNodeId);
      } else if (targetType === 'workflow' && targetNodeId) {
        this.loadWorkflow(targetNodeId);
      } else {
        console.warn('⚠️ No valid target found for attempts condition');
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: any) {
    if (this.fields.fieldName) {
      this.closeModal('connection_modal_' + this.fields.fieldName);
    }
  }

  closeOnBackdropClick(event: MouseEvent, modalId: string) {
    const modal = document.getElementById(modalId);
    if (event.target === modal) {
      this.closeModal(modalId);
    }
  }

  closeModal(modalId: string) {
    const modal = document.getElementById(modalId);
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
    this.stopPollingFromModalRenderer();
    this.loadingCards.clear();
  }

  stopPollingFromModalRenderer() {
    if (this.modalRendererRef) {
      this.modalRendererRef.stopChildPolling();
    }
  }

  onModalFormSaved(event: any): void {
    const optionId = this.activeModalContext?.selectedOption?._id;
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');

    if (event?.success) {
      const option = this.activeModalContext?.selectedOption;
      if (option && option.name) {
        console.log(`✅ Connection completed for: ${option.name}`);
      }

      const { nextNodeId, targetValue, targetWorkflowId, isSseTrigger } =
        this.resolveNavigationTargets(this.currentNode);

      const currentIndex = this.workflowData?.nodes?.findIndex(
        (n: any) => n._id === this.currentNode?._id,
      );
      const lastNode = this.workflowData?.nodes?.findIndex(
        (n: any) => n._id === nextNodeId,
      );
      const lastNodeIndex = this.workflowData?.nodes?.length - 1;
      const nextNodeIndex = this.workflowData?.nodes?.findIndex(
        (n: any) => n._id === nextNodeId,
      );
      const isLastNode = currentIndex === lastNodeIndex;
      const isSecondOrThirdNode =
        currentIndex === 1 ||
        currentIndex === 2 ||
        currentIndex == lastNodeIndex;
      const isNextNodeTheLastNode = nextNodeIndex === lastNodeIndex;

      // 🔥 NEW: Mark selected when NEXT NODE is the LAST node
      if (isNextNodeTheLastNode && !isLastNode) {
        const newValue = [this.activeModalContext?.selectedOption?._id];
        const newTags = [this.activeModalContext?.selectedOption?.name];

        this.control?.setValue(newValue);
        tagsControl?.setValue(newTags);

        this.currentNode.selectedOptions = newValue;
        this.workflowData.selectedOptions = newValue;

        this.control?.markAsTouched();
        this.control?.updateValueAndValidity();

        this.loadingCards.delete(optionId);
      }

      // ✅ When reaching last node
      if (isLastNode) {
        const newValue = [this.activeModalContext?.selectedOption?._id];
        const newTags = [this.activeModalContext?.selectedOption?.name];

        this.control?.setValue(newValue);
        tagsControl?.setValue(newTags);
        this.selectedOptions = [...newValue];

        this.control?.markAsTouched();
        this.control?.updateValueAndValidity();

        this.loadingCards.delete(optionId);
        this.currentNode.selectedOptions = this.selectedOptions;
        this.workflowData.selectedOptions = this.selectedOptions;
        this.selectedOptions = [...newValue];
      }

      // ✅ Handle workflow transitions
      if (nextNodeId && !isSseTrigger) {
        this.loadNode(nextNodeId);
        return;
      }

      if (targetValue) {
        const token = localStorage.getItem('accessToken');
        if (token) {
          window.location.href = `${targetValue}/${token}`;
          return;
        }
      }

      if (targetWorkflowId) {
        this.loadWorkflow(targetWorkflowId);
        return;
      }

      // 🔁 SSE Trigger
      if (isSseTrigger) {
        const sseTrigger = this.currentNode?.triggers?.find(
          (trigger: any) => trigger.event === 'listenSSE',
        );
        if (sseTrigger?.config?.url && sseTrigger?.config?.nextNodeId) {
          const apiEndpoint = environment.api_endpoint;
          const authToken = localStorage.getItem('accessToken') || '';
          const resolvedUrl = sseTrigger.config.url
            .replace('{{api_endpoint}}', apiEndpoint)
            .replace('{{authToken}}', authToken);

          // this.SseService.initSSE(resolvedUrl);
          this.loadNode(sseTrigger.config.nextNodeId);
          return;
        }
      }

      // ✅ Close modal properly
      const modalId = `connection_modal_${this.fields.fieldName}`;
      const modal = document.getElementById(modalId);
      if (modal) modal.classList.remove('show');
      this.activeModalContext = null;
    }
  }

  /** Resolves navigation targets from current node */
  resolveNavigationTargets(node: any): {
    nextNodeId: string | null;
    targetValue: string | null;
    targetWorkflowId: string | null;
    recordId: string | null;
    isSseTrigger?: boolean;
  } {
    const fallback = {
      nextNodeId: null,
      targetValue: null,
      targetWorkflowId: null,
      recordId: node?.recordId ?? null,
      isSseTrigger: false,
    };

    if (!node?.next || !Array.isArray(node.next)) return fallback;

    const nextButtonObj =
      node.next.find((n: any) =>
        ['next_button', 'submit_continue'].includes(n.buttonId),
      ) || node.next[0];

    if (!nextButtonObj) return fallback;

    const resolveSseTrigger = (): {
      nextNodeId: string | null;
      isSseTrigger: boolean;
    } => {
      const sseTrigger = node.triggers?.find(
        (trigger: any) => trigger.event === 'listenSSE',
      );
      if (sseTrigger?.config?.nextNodeId) {
        return { nextNodeId: sseTrigger.config.nextNodeId, isSseTrigger: true };
      }
      return { nextNodeId: null, isSseTrigger: false };
    };

    const condition = nextButtonObj.condition;

    if (Array.isArray(condition)) {
      for (const cond of condition) {
        if (cond?.targetType === 'node' && cond?.targetValue) {
          return { ...fallback, nextNodeId: cond.targetValue };
        }
        if (cond?.targetNodeId) {
          return { ...fallback, nextNodeId: cond.targetNodeId };
        }
        if (cond?.targetType === 'url' && cond?.targetValue) {
          return { ...fallback, targetValue: cond.targetValue };
        }
        if (cond?.targetType === 'workflow' && cond?.targetValue) {
          return { ...fallback, targetWorkflowId: cond.targetValue };
        }
        if (cond?.targetType === 'manual') {
          const { nextNodeId, isSseTrigger } = resolveSseTrigger();
          if (nextNodeId) return { ...fallback, nextNodeId, isSseTrigger };
        }
      }
    } else if (typeof condition === 'object' && condition !== null) {
      if (nextButtonObj.targetType === 'node' && nextButtonObj.targetValue) {
        return { ...fallback, nextNodeId: nextButtonObj.targetValue };
      }
      if (nextButtonObj.targetNodeId) {
        return { ...fallback, nextNodeId: nextButtonObj.targetNodeId };
      }
      if (nextButtonObj.targetType === 'url' && nextButtonObj.targetValue) {
        return { ...fallback, targetValue: nextButtonObj.targetValue };
      }
      if (nextButtonObj.targetType === 'manual') {
        const { nextNodeId, isSseTrigger } = resolveSseTrigger();
        if (nextNodeId) return { ...fallback, nextNodeId, isSseTrigger };
      }
    }
    return fallback;
  }
  /** Function to load the form with the formId */
  loadForm(formId: string, recordId?: any) {
    const request = this.formService.loadFormData(formId, recordId);
    request.subscribe({
      next: (data) => {
        this.formData = data.data;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.cdr.detectChanges();
      },
    });
    return request; // Return the observable so we can chain it
  }
}
